<template>
  <generic-modal
    @close="$emit('close')"
    @submit="createTicket"
    :title="'Create Maintenance Ticket for address: ' + address"
    width="600px"
    :disabled="!form.installer || !form.type || !form.description || !form.category"
  >
    <div v-if="ticketDetail?.id">
      <div class="tw-text-gray-500 tw-text-xs tw-font-bold">
        Installatieticket:
        <!-- TODO: replace hardcoded URL with ticketsBaseUrl from '@/logic/common/urls' -->
        <a
          :href="`https://tickets.norm.eu/v2/tickets/${ticketDetail?.id}`"
          class="hover:tw-underline tw-text-l tw-cursor-pointer"
          target="_blank"
        >
          {{ ticketDetail?.id }}
        </a>
      </div>
      <div class="tw-text-gray-500 tw-text-xs tw-font-bold">
        Partner: {{ ticketDetail?.data?.partner }}
      </div>
      <div
        class="tw-text-gray-500 tw-text-xs tw-font-bold"
        :class="{
          'tw-text-red-500':
            differenceInYears(new Date(), parseISO(ticketDetail.appointment_date)) < 1,
        }"
      >
        Afspraak: {{ formatDate(ticketDetail.appointment_date) }}
      </div>
      <div class="tw-text-gray-500 tw-text-xs tw-font-bold">
        Daktype: {{ ticketDetail?.data?.input.extra_fields.roof_type }}
      </div>
      <div class="tw-text-gray-500 tw-text-xs tw-font-bold">
        Afbeeldingen van installatieticket en design task worden automatisch toegevoegd aan het
        onderhoudsticket
      </div>
    </div>
    <div v-else class="tw-text-gray-500 tw-text-xs tw-font-bold">
      Geen installatieticket gevonden voor dit adres
    </div>
    <div class="tw-text-gray-500 tw-text-xs tw-font-bold">
      Project: {{ resolvedProject || 'onbekend' }}
    </div>
    <form-field
      type="textarea"
      label="Title"
      rows="1"
      v-model="form.title"
      maxlength="70"
      placeholder="Niet te lang! max 70 karakters"
    />
    <form-field type="textarea" label="Description" rows="3" v-model="form.description" />
    <div class="tw-flex tw-flex-column tw-gap-5">
      <form-field
        label="Service team"
        :options="partners"
        null-option="Select service team"
        type="select"
        v-model="form.installer"
      />
      <form-field
        label="Category"
        :options="categories"
        type="select"
        v-model="form.category"
        null-option="Select category"
      />
    </div>
    <div class="tw-flex tw-flex-column tw-gap-5">
      <form-field
        label="Maintenance type"
        :options="maintenanceTypes"
        null-option="Select type"
        type="select"
        v-model="form.type"
      />
      <form-field label="Priority" :options="priority" type="select" v-model="form.priority" />
    </div>
    <div class="tw-flex tw-flex-column tw-gap-5">
      <form-field
        label="Ref number for billing"
        v-model="form.refNumber"
        placeholder="Ref number for billing"
      />
      <form-field label="Number of people needed" type="number" v-model="form.teamSize" />
    </div>
    <form-field type="textarea" label="Notes about key/access" v-model="form.note" />
  </generic-modal>
</template>

<script setup>
import { watch, ref, computed } from 'vue'
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import fetchData from '@/util/fetch'
import useConfigProjects from '@/util/useConfigProjects'
import { formatDate, notifications } from '@/util'
import FormField from '@/components/shared/FormField.vue'
import GenericModal from '@/components/shared/GenericModal.vue'
import { parseISO, differenceInYears } from 'date-fns'

const props = defineProps({
  caseDetail: {
    type: Object,
    required: true,
  },
})

const emit = defineEmits(['close'])

const queryClient = useQueryClient()
const id = computed(() => props.caseDetail.id)
const address = computed(() => props.caseDetail.address)
const category = computed(() => props.caseDetail.category || 'O&M')
const comment = ref('')

const form = ref({
  installer: '',
  priority: 'Normal',
  category: category.value,
  refNumber: '',
  teamSize: 1,
  note: '',
  type: '',
  description: '',
  title: props.caseDetail.description,
})

watch(
  () => props.caseDetail.id,
  () => {
    form.description = ''
    form.title = props.caseDetail.description
    form.category = props.caseDetail.category || 'O&M'

    comment.value = {}
  }
)

const getPartners = () => fetchData('/tickets/api/v1/partners')
const partnersQuery = useQuery({
  queryKey: ['partners'],
  queryFn: getPartners,
})
const partners = computed(() => partnersQuery.data?.value?.data.map((p) => p.legacy_name) || [])

const { data: installationTicketQuery } = useQuery({
  queryKey: computed(() => ['installationTicket', address.value]),
  keepPreviousData: true,
  queryFn: async () => {
    try {
      const formattedAddress = address.value.replace(/\s+/g, '').toLowerCase()
      const result = await fetchData(
        `/tickets/api/v1/tickets?view=list&query=${formattedAddress}&type=installation&status=approved`
      )
      return result.data
    } catch (error) {
      console.error('Error fetching comments:', error)
      throw error
    }
  },
})

const oldestInstallationTicket = computed(() => {
  if (installationTicketQuery?.value?.length === 0) return null
  return installationTicketQuery?.value?.reduce((prev, current) =>
    prev.id < current.id ? prev : current
  )
})

// Fetch ticket details based on the installation ticket
const { data: ticketDetail } = useQuery({
  queryKey: computed(() => ['ticket', oldestInstallationTicket.value?.id]),
  queryFn: async () => {
    if (!oldestInstallationTicket.value) {
      return null
    }

    const url = `/tickets/api/v1/tickets/${oldestInstallationTicket.value.id}`
    const result = await fetchData(url)
    return result.data
  },
  refetchOnWindowFocus: false,
  enabled: computed(() => !!oldestInstallationTicket.value),
})

// Fetch address details if there is no older installation ticket
const { data: addressDetail } = useQuery({
  queryKey: computed(() => ['address', address.value]),
  queryFn: async () => {
    const url = `/api/v1/data/address/${address.value}`
    const result = await fetchData(url)
    return result.data
  },
  refetchOnWindowFocus: false,
})

// The case's location knows its own project; the address resolver is only a
// fallback (a multi-project address would otherwise get an arbitrary project)
const { projectNamesById } = useConfigProjects()
const caseProjectName = computed(() => {
  const projectId = props.caseDetail.location?.project_id
  if (projectId == null) return null
  return projectNamesById.value[projectId] || null
})
const resolvedProject = computed(() => caseProjectName.value || addressDetail?.value?.project || '')

const maintenanceTypes = [
  'Storing',
  'Service',
  'Lekkage',
  'Schadeherstel',
  'Herstel inspectie',
  'Installatiegarantie',
]
const categories = ['O&M', 'KAM', 'OPS', 'Klantenservice', 'Alarmbellen']
const priority = ['Leak (6 hours)', 'Urgent', 'Normal']

const { mutate: createTicket } = useMutation({
  mutationFn: () => {
    let productsList = []
    let images = []
    let roofAngle = ''
    let roofType = ''

    const ticketData = ticketDetail.value?.data
    if (ticketData) {
      const productsSource = Object.values(ticketData.output.products).some(
        (product) => Object.keys(product).length > 0
      )
        ? ticketData.output.products
        : ticketData.input.products

      productsList = Object.entries(productsSource).flatMap(([type, items]) =>
        Object.entries(items)
          .filter((item) => item[1]?.amount >= 1)
          .map(([code, details]) => ({
            code: code,
            amount: Number(details.amount),
            type: type,
            is_installed: true,
          }))
      )

      images = [...ticketData.input.images, ...ticketData.output.images]
        .filter((image) => image.key !== null && image.key !== undefined)
        .map((image) => ({
          url: image.key,
          type: image.type,
        }))
      roofAngle = ticketData.input.extra_fields.roof_angle
      roofType = ticketData.input.extra_fields.roof_type
    } else if (addressDetail?.value?.tasks?.design) {
      const designTask = addressDetail?.value?.tasks?.design

      productsList = designTask.products.filter((product) => product.amount >= 1 && product.code)
      images =
        designTask.images
          .filter((image) => image.key && image.key !== null && image.key !== undefined)
          .map((image) => ({
            url: image.key,
            type: image.type,
          })) || []
      roofAngle = designTask.extra_fields?.roof_angle
      roofType = designTask.extra_fields?.roof_type
    }
    const project = caseProjectName.value || addressDetail?.value?.project
    const salesTask = addressDetail?.value?.tasks?.sales
    const panelsSigned =
      salesTask?.panel_quantity != null && salesTask?.signed_at != null
        ? { amount: salesTask.panel_quantity, date: salesTask.signed_at }
        : null
    const client = {
      address: addressDetail?.value?.address,
      ...addressDetail?.value?.client,
      panels_signed: panelsSigned,
    }

    if (!project) {
      notifications.addNotification({
        message: `Geen installatieticket of design task gevonden, kan geen ticket maken`,
        type: 'danger',
      })
    }

    if (!form.value.category) {
      notifications.addNotification({
        message: `Kan geen ticket maken zonder categorie`,
        type: 'danger',
      })
    }

    const payload = {
      type: 'maintenance',
      partner: form.value.installer,
      client_info: client,
      images: images,
      extra_fields: {
        priority: form.value.priority,
        title: form.value.title,
        description: form.value.description,
        requiredTeamSize: form.value.teamSize,
        roof_angle: roofAngle,
        roof_type: roofType,
        category: form.value.category,
      },
      finance: {
        client_reference: form.value.refNumber,
        malfunction_category: form.value.type,
      },
      case_id: id.value,
      project_name: project,
      note: form.value.note,
      products: productsList,
    }

    return fetchData('/tickets/api/v1/tickets', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },
  onError: (e) => {
    console.error('Error on creating ticket', e)
    notifications.addNotification({
      message: `Something went wrong while creating the ticket with error: ${e.message}`,
      type: 'danger',
    })
  },
  onSuccess: () => {
    form.value = {
      installer: '',
      priority: 'Normal',
      refNumber: '',
      teamSize: 1,
      note: '',
      type: 'Defect',
      description: '',
      title: '',
    }

    notifications.addNotification({
      message: 'Ticket created successfully',
      type: 'success',
    })

    emit('close')

    queryClient.invalidateQueries({ queryKey: ['tickets'] })
    queryClient.invalidateQueries({ queryKey: ['cases'] })
  },
})
</script>
